import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Users as UsersIcon } from "lucide-react";
import { PageHeader, SearchInput, AppDataTable } from "../../ui";
import { FilterBar, FilterSelect } from "../../ui/FilterBar";
import UserRow from "./UserRow";
import apiClient from "../../../utils/apiClient";
import "./users.scss";

const ROLE_OPTIONS = [
  { value: "all", label: "All Roles" },
  { value: "tenantadmin", label: "Tenant Admin" },
  { value: "superadmin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "instructor", label: "Instructor" },
  { value: "student", label: "Student" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const PAGE_SIZE = 25;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [instituteFilter, setInstituteFilter] = useState("all");
  const [institutes, setInstitutes] = useState([]);

  // Debounce search
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, roleFilter, statusFilter, instituteFilter]);

  // Load institutes for filter dropdown (lightweight — fetch high limit once)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/sa/institutes", { params: { limit: 1000, page: 1 } });
        setInstitutes(res.data.institutes || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const instituteOptions = useMemo(() => ([
    { value: "all", label: "All Institutes" },
    { value: "__none__", label: "No Institute" },
    ...institutes
      .map((i) => ({ value: i._id, label: i.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]), [institutes]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = { page, limit: PAGE_SIZE };
      if (roleFilter !== "all") params.role = roleFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (instituteFilter !== "all") params.instituteId = instituteFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await apiClient.get("/sa/users", { params });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch {
      setUsers([]);
      setTotal(0);
      setPages(1);
    }
  }, [page, roleFilter, statusFilter, instituteFilter, debouncedSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return (
    <div className="sa-users app-page-fill">
      <PageHeader
        title="Users"
        description={`${total} users across all institutes`}
        icon={UsersIcon}
      />

      <FilterBar
        actions={
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email, phone…" />
        }
      >
        <FilterSelect label="Role" value={roleFilter} onChange={setRoleFilter} options={ROLE_OPTIONS} />
        <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
        <FilterSelect label="Institute" value={instituteFilter} onChange={setInstituteFilter} options={instituteOptions} />
      </FilterBar>

      <AppDataTable
        columns={[
          { key: "user", label: "User" },
          { key: "role", label: "Role" },
          { key: "institute", label: "Institute" },
          { key: "status", label: "Status" },
          { key: "lastLogin", label: "Last Login" },
        ]}
        isEmpty={users.length === 0}
        emptyMessage="No users match your search"
        pagination={{
          page,
          totalPages: pages,
          total,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "users",
          showSinglePage: true,
        }}
      >
        {users.map((user) => <UserRow key={user._id} user={user} />)}
      </AppDataTable>
    </div>
  );
};

export default UsersPage;
